import React, { useState, useEffect, useCallback } from 'react';
import { DndContext, closestCenter, PointerSensor, useSensor } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import WysiwygEditor from '../WysiwygEditor';
import {
  CloudUpload,
  Plus,
  Trash2,
  Copy,
  ChevronUp,
  ChevronDown,
  Images,
  FileText,
  ClipboardList,
  CircleQuestionMark,
  Palette
} from 'lucide-react';

// For static assets, use ASSET_URL in dev, hostname in prod
const ASSET_BASE_URL = process.env.REACT_APP_ASSET_URL || process.env.REACT_APP_HOSTNAME || '';

// Sortable FAQ Item Component
function SortableFaqItem({ faq, index, onUpdate, onDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: faq.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`border border-gray-200 rounded-lg p-3 ${isDragging ? 'z-50' : ''}`}
    >
      <div className="flex justify-between items-start">
        <div className="flex-1 space-y-2">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Question {index + 1}
            </label>
            <input
              type="text"
              value={faq.question}
              onChange={(e) => onUpdate(faq.id, { question: e.target.value })}
              className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Answer
            </label>
            <textarea
              value={faq.answer}
              onChange={(e) => onUpdate(faq.id, { answer: e.target.value })}
              className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
              rows={2}
            />
          </div>
        </div>
        <div className="flex items-center ml-2 space-x-1">
          <button
            type="button"
            {...attributes}
            {...listeners}
            className="p-1 text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing"
            title="Drag to reorder"
          >
            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M3 6h18v2H3V6zm0 5h18v2H3v-2zm0 5h18v2H3v-2z"/>
            </svg>
          </button>
          <button
            type="button"
            onClick={() => onDelete(faq.id)}
            className="p-1 text-red-500 hover:text-red-700"
            title="Delete FAQ"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function SortableSection({ 
  section, 
  isCollapsed, 
  onToggleCollapse, 
  onUpdate, 
  onDelete, 
  onDuplicate,
  uploadingBackground, 
  onBackgroundUpload,
  sectionFaqs,
  newFaqs,
  setNewFaqs,
  handleAddFaq,
  handleUpdateFaq,
  handleDeleteFaq,
  handleReorderFaqs,
  faqSensor,
  sectionGallery,
  uploadingGalleryImage,
  handleGalleryImageUpload,
  handleDeleteGalleryImage,
  handleUpdateGalleryImage
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const sectionInfo = {
    banner: {
      title: 'Banner',
      description: 'Background image section',
      icon: Images,
      color: 'bg-purple-100 text-purple-800'
    },
    content: {
      title: 'Content',
      description: 'About section with optional form',
      icon: FileText,
      color: 'bg-blue-100 text-blue-800'
    },
    forms: {
      title: 'Form',
      description: 'Entry form section',
      icon: ClipboardList,
      color: 'bg-green-100 text-green-800'
    },
    faqs: {
      title: 'FAQs',
      description: 'Frequently asked questions',
      icon: CircleQuestionMark,
      color: 'bg-yellow-100 text-yellow-800'
    },
    gallery: {
      title: 'Gallery',
      description: 'Image gallery section',
      icon: Palette,
      color: 'bg-pink-100 text-pink-800'
    }
  }[section.type] || { title: section.type, description: '', icon: FileText, color: 'bg-gray-100 text-gray-800' };
  const SectionIcon = sectionInfo.icon;

  const hasContent = ['banner', 'content', 'faqs'].includes(section.type);

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className={`rounded-lg border-2 border-gray-200 ${sectionInfo.color} overflow-hidden ${
        isDragging ? 'opacity-50' : ''
      }`}
    >
      <div className="relative">
        <div {...listeners} className="p-4 cursor-move border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 flex-1 pr-4">
              <SectionIcon className="w-6 h-6" />
              <div className="flex-1">
                <h5 className="font-semibold">{sectionInfo.title}</h5>
                <p className="text-sm opacity-75">{sectionInfo.description}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2 ml-4">
              {hasContent && (
                <button
                  type="button"
                  onClick={() => onToggleCollapse(section.id)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-md"
                  title={isCollapsed ? "Expand section" : "Collapse section"}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  {isCollapsed ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronUp className="w-4 h-4" />
                  )}
                </button>
              )}
              <button
                type="button"
                onClick={() => onDuplicate(section)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-md"
                title="Duplicate section"
                onMouseDown={(e) => e.stopPropagation()}
              >
                <Copy className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => onDelete(section.id)}
                className="p-2 text-red-400 hover:text-red-600 hover:bg-white rounded-md"
                title="Delete section"
                onMouseDown={(e) => e.stopPropagation()}
              >
                <Trash2 className="w-4 h-4" />
              </button>
              <svg className="w-5 h-5 opacity-50 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
              </svg>
            </div>
          </div>
        </div>
      </div>
      
      {/* Section content */}
      {section.type === 'banner' && !isCollapsed && (
        <div className="px-4 py-4 bg-white/50">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Background Image/Video</label>
              {section.background_image_url && (
                <div className="mb-4">
                  {section.background_image_url.match(/\\.(mp4|webm|ogg)$/i) ? (
                    <video 
                      src={`${ASSET_BASE_URL}${section.background_image_url}`} 
                      className="h-32 w-full object-cover rounded-lg"
                      controls
                      muted
                    />
                  ) : (
                    <img 
                      src={`${ASSET_BASE_URL}${section.background_image_url}`} 
                      alt="Background" 
                      className="h-32 w-full object-cover rounded-lg"
                    />
                  )}
                </div>
              )}
              <div className="flex items-center space-x-4">
                <label className="cursor-pointer px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">
                  <span>{uploadingBackground ? 'Uploading...' : 'Upload Background'}</span>
                  <input
                    type="file"
                    className="sr-only"
                    accept="image/jpeg,image/jpg,image/png,image/gif,video/mp4,video/webm,video/ogg"
                    onChange={(e) => onBackgroundUpload(e, section.id)}
                    disabled={uploadingBackground}
                  />
                </label>
                {section.background_image_url && (
                  <button
                    type="button"
                    onClick={() => onUpdate(section.id, { background_image_url: '' })}
                    className="text-red-600 hover:text-red-700"
                  >
                    Remove
                  </button>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Supported formats: JPG, PNG, GIF (max 10MB) or MP4, WebM, OGG (max 50MB)
              </p>
            </div>
            
            <div className="flex items-center">
              <input
                type="checkbox"
                id={`full_width_background_${section.id}`}
                checked={section.full_width_background === true}
                onChange={(e) => onUpdate(section.id, { full_width_background: e.target.checked })}
                className="h-4 w-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
              />
              <label htmlFor={`full_width_background_${section.id}`} className="ml-2 text-sm text-gray-700">
                Full width background
              </label>
            </div>
          </div>
        </div>
      )}
      
      {section.type === 'content' && !isCollapsed && (
        <div className="px-4 py-4 bg-white/50">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">About Section Header</label>
              <input
                type="text"
                value={section.about_header || ''}
                onChange={(e) => onUpdate(section.id, { about_header: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder="About This Campaign"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">About Section Description</label>
              <WysiwygEditor
                value={section.about_description || ''}
                onChange={(html) => onUpdate(section.id, { about_description: html })}
                placeholder="Enter description for the about section..."
              />
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id={`about_with_form_${section.id}`}
                checked={section.about_with_form === true}
                onChange={(e) => onUpdate(section.id, { about_with_form: e.target.checked })}
                className="h-4 w-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
              />
              <label htmlFor={`about_with_form_${section.id}`} className="ml-2 text-sm text-gray-700">
                Show entry form in this section
              </label>
            </div>
          </div>
        </div>
      )}
      
      {section.type === 'faqs' && !isCollapsed && (
        <div className="px-4 py-4 bg-white/50">
          <div className="space-y-6">
            {/* FAQ Section Header and Description */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">FAQ Section Header</label>
                <input
                  type="text"
                  value={section.faqs_header || ''}
                  onChange={(e) => onUpdate(section.id, { faqs_header: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="Frequently Asked Questions"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">FAQ Section Description</label>
                <WysiwygEditor
                  value={section.faqs_description || ''}
                  onChange={(html) => onUpdate(section.id, { faqs_description: html })}
                  placeholder="Enter description for the FAQ section..."
                />
              </div>
            </div>

            {/* FAQ Management */}
            <div className="border-t pt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-4">Manage FAQs</h4>
              
              {/* Add New FAQ */}
              <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                <h5 className="text-xs font-medium text-gray-600 mb-3">Add New FAQ</h5>
                <div className="space-y-3">
                  <input
                    type="text"
                    value={newFaqs[section.id]?.question || ''}
                    onChange={(e) => setNewFaqs(prev => ({
                      ...prev,
                      [section.id]: { ...prev[section.id], question: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    placeholder="Enter question..."
                  />
                  <textarea
                    value={newFaqs[section.id]?.answer || ''}
                    onChange={(e) => setNewFaqs(prev => ({
                      ...prev,
                      [section.id]: { ...prev[section.id], answer: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    rows={2}
                    placeholder="Enter answer..."
                  />
                  <button
                    type="button"
                    onClick={() => handleAddFaq(section.id)}
                    className="px-3 py-1.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 text-sm"
                  >
                    Add FAQ
                  </button>
                </div>
              </div>

              {/* Existing FAQs - Sortable */}
              <div>
                {(sectionFaqs[section.id] || []).length > 0 ? (
                  <DndContext
                    sensors={[faqSensor]}
                    collisionDetection={closestCenter}
                    onDragEnd={(event) => handleReorderFaqs(section.id, event)}
                  >
                    <SortableContext
                      items={(sectionFaqs[section.id] || []).map(faq => faq.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="space-y-3">
                        {(sectionFaqs[section.id] || []).map((faq, index) => (
                          <SortableFaqItem
                            key={faq.id}
                            faq={faq}
                            index={index}
                            onUpdate={(faqId, data) => handleUpdateFaq(section.id, faqId, data)}
                            onDelete={(faqId) => handleDeleteFaq(section.id, faqId)}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                ) : (
                  <p className="text-sm text-gray-500 italic text-center py-4">
                    No FAQs added yet. Add your first FAQ above.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {section.type === 'gallery' && !isCollapsed && (
        <div className="px-4 py-4 bg-white/50">
          <div className="space-y-4">
            {/* Gallery Management */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-4">Manage Gallery Images</h4>
              
              {/* Upload New Image */}
              <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                <h5 className="text-xs font-medium text-gray-600 mb-3">Add New Image</h5>
                <div className="flex items-center space-x-4">
                  <label className={`cursor-pointer px-4 py-2 rounded-lg font-medium ${
                    uploadingGalleryImage ? 'bg-gray-300 cursor-not-allowed' : 'bg-teal-600 hover:bg-teal-700 cursor-pointer'
                  } text-white`}>
                    <span>{uploadingGalleryImage ? 'Uploading...' : 'Upload Image'}</span>
                    <input
                      type="file"
                      className="sr-only"
                      accept="image/jpeg,image/jpg,image/png,image/gif"
                      onChange={(e) => {
                        e.preventDefault();
                        handleGalleryImageUpload(e, section.id);
                      }}
                      disabled={uploadingGalleryImage}
                    />
                  </label>
                  <p className="text-xs text-gray-500">
                    Supported formats: JPG, PNG, GIF (max 10MB)
                  </p>
                </div>
              </div>

              {/* Existing Gallery Images */}
              <div>
                {(sectionGallery[section.id] || []).length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {(sectionGallery[section.id] || []).map((image, index) => (
                      <div key={image.id} className="relative group">
                        <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                          <img
                            src={`${ASSET_BASE_URL}${image.image_url}`}
                            alt={image.title || `Gallery image ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        
                        {/* Image overlay with controls */}
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 rounded-lg flex items-center justify-center">
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex space-x-2">
                            <button
                              type="button"
                              onClick={() => handleDeleteGalleryImage(section.id, image.id)}
                              className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                              title="Delete image"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                        
                        {/* Image details form */}
                        <div className="mt-2 space-y-2">
                          <input
                            type="text"
                            value={image.title || ''}
                            onChange={(e) => handleUpdateGalleryImage(section.id, image.id, { title: e.target.value })}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                            placeholder="Image title..."
                          />
                          <textarea
                            value={image.description || ''}
                            onChange={(e) => handleUpdateGalleryImage(section.id, image.id, { description: e.target.value })}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                            rows={2}
                            placeholder="Image description..."
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic text-center py-8">
                    No images added yet. Upload your first image above.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const SectionManager = ({ 
  campaignId,
  sections = [],
  onSectionsChange,
  sensors,
  api,
  onSaveFaqChanges,
  onSaveGalleryChanges
}) => {
  const [collapsedSections, setCollapsedSections] = useState({});
  const [uploadingBackground, setUploadingBackground] = useState(false);
  const [showAddDropdown, setShowAddDropdown] = useState(false);
  const [localSections, setLocalSections] = useState(sections); // Local state for unsaved changes
  const [sectionFaqs, setSectionFaqs] = useState({}); // FAQs for each section
  const [newFaqs, setNewFaqs] = useState({}); // New FAQ input for each section
  const [sectionGallery, setSectionGallery] = useState({}); // Gallery images for each section
  const [uploadingGalleryImage, setUploadingGalleryImage] = useState(false);

  // Create FAQ drag sensor at component level
  const faqSensor = useSensor(PointerSensor, {
    activationConstraint: {
      distance: 8,
    },
  });

  // Update local sections when sections prop changes
  useEffect(() => {
    setLocalSections(sections);
  }, [sections]);

  // Load FAQs for each FAQ section
  useEffect(() => {
    const faqSections = localSections.filter(section => section.type === 'faqs');
    faqSections.forEach(section => {
      if (!sectionFaqs[section.id]) {
        fetchSectionFaqs(section.id);
      }
    });
  }, [localSections]);

  // Load Gallery images for each gallery section
  useEffect(() => {
    const gallerySections = localSections.filter(section => section.type === 'gallery');
    gallerySections.forEach(section => {
      if (!sectionGallery[section.id]) {
        fetchSectionGallery(section.id);
      }
    });
  }, [localSections]);

  const fetchSectionFaqs = async (sectionId) => {
    try {
      const response = await api.get(`/admin/campaigns/${campaignId}/campaign-sections/${sectionId}/faqs`);
      setSectionFaqs(prev => ({
        ...prev,
        [sectionId]: response.data.faqs || []
      }));
    } catch (error) {
      console.error('Failed to fetch section FAQs:', error);
    }
  };

  const fetchSectionGallery = async (sectionId) => {
    try {
      const response = await api.get(`/admin/campaigns/${campaignId}/campaign-sections/${sectionId}/gallery`);
      setSectionGallery(prev => ({
        ...prev,
        [sectionId]: response.data.gallery || []
      }));
    } catch (error) {
      console.error('Failed to fetch section gallery:', error);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showAddDropdown && !event.target.closest('.add-section-dropdown')) {
        setShowAddDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAddDropdown]);

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = localSections.findIndex(section => section.id === active.id);
      const newIndex = localSections.findIndex(section => section.id === over.id);
      
      const newSections = arrayMove(localSections, oldIndex, newIndex);
      
      // Update sort orders
      const updatedSections = newSections.map((section, index) => ({
        ...section,
        sort_order: index
      }));
      
      setLocalSections(updatedSections);
      onSectionsChange(updatedSections); // Still update parent for save
    }
  };

  const handleAddSection = async (type) => {
    try {
      const response = await api.post(`/admin/campaigns/${campaignId}/campaign-sections`, {
        type,
        data: {}
      });
      
      const newSections = [...localSections, response.data.section];
      setLocalSections(newSections);
      onSectionsChange(newSections);
      setShowAddDropdown(false); // Close dropdown after adding
    } catch (error) {
      console.error('Failed to add section:', error);
      alert('Failed to add section');
    }
  };

  const handleUpdateSection = (sectionId, data) => {
    // Update local state only - no API call until save
    const updatedSections = localSections.map(section => 
      section.id === sectionId ? { ...section, ...data } : section
    );
    setLocalSections(updatedSections);
    onSectionsChange(updatedSections); // Update parent state for save
  };

  const handleDeleteSection = async (sectionId) => {
    if (window.confirm('Are you sure you want to delete this section?')) {
      try {
        await api.delete(`/admin/campaigns/${campaignId}/campaign-sections/${sectionId}`);
        
        const updatedSections = localSections.filter(section => section.id !== sectionId);
        setLocalSections(updatedSections);
        onSectionsChange(updatedSections);
      } catch (error) {
        console.error('Failed to delete section:', error);
        alert('Failed to delete section');
      }
    }
  };

  const handleDuplicateSection = async (section) => {
    try {
      const duplicateData = {
        background_image_url: section.background_image_url,
        full_width_background: section.full_width_background,
        about_header: section.about_header ? `${section.about_header} (Copy)` : null,
        about_description: section.about_description,
        about_with_form: section.about_with_form,
        faqs_header: section.faqs_header ? `${section.faqs_header} (Copy)` : null,
        faqs_description: section.faqs_description
      };

      const response = await api.post(`/admin/campaigns/${campaignId}/campaign-sections`, {
        type: section.type,
        data: duplicateData
      });
      
      const newSections = [...localSections, response.data.section];
      setLocalSections(newSections);
      onSectionsChange(newSections);
    } catch (error) {
      console.error('Failed to duplicate section:', error);
      alert('Failed to duplicate section');
    }
  };

  const handleBackgroundUpload = async (e, sectionId) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    const validVideoTypes = ['video/mp4', 'video/webm', 'video/ogg'];
    const validTypes = [...validImageTypes, ...validVideoTypes];
    
    if (!validTypes.includes(file.type)) {
      alert('Please upload an image (JPG, PNG, GIF) or video (MP4, WebM, OGG) file');
      return;
    }
    
    // Check file size (50MB for videos, 10MB for images)
    const maxSize = validVideoTypes.includes(file.type) ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) {
      alert(`File size must be less than ${validVideoTypes.includes(file.type) ? '50MB' : '10MB'}`);
      return;
    }

    setUploadingBackground(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(`/admin/campaigns/${campaignId}/campaign-sections/${sectionId}/upload-background`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Update the local sections state
      const updatedSections = localSections.map(section => 
        section.id === sectionId 
          ? { ...section, background_image_url: response.data.fileUrl }
          : section
      );
      setLocalSections(updatedSections);
      onSectionsChange(updatedSections);
      
      // Clear the input
      e.target.value = '';
    } catch (error) {
      console.error('Upload error:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error occurred';
      alert('Failed to upload background: ' + errorMessage);
    } finally {
      setUploadingBackground(false);
    }
  };

  // FAQ Management Functions
  const handleAddFaq = (sectionId) => {
    const newFaq = newFaqs[sectionId] || { question: '', answer: '' };
    
    if (!newFaq.question.trim() || !newFaq.answer.trim()) {
      alert('Please enter both question and answer');
      return;
    }

    // Create a temporary FAQ with a temp ID - will be saved when "Save Theme & Layout" is clicked
    const tempFaq = {
      id: `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      question: newFaq.question.trim(),
      answer: newFaq.answer.trim(),
      sort_order: (sectionFaqs[sectionId] || []).length
    };

    // Update local FAQs state
    setSectionFaqs(prev => ({
      ...prev,
      [sectionId]: [...(prev[sectionId] || []), tempFaq]
    }));

    // Clear the form
    setNewFaqs(prev => ({
      ...prev,
      [sectionId]: { question: '', answer: '' }
    }));
  };

  const handleUpdateFaq = (sectionId, faqId, updatedFaq) => {
    // Update local FAQs state only - no API call until save
    setSectionFaqs(prev => ({
      ...prev,
      [sectionId]: (prev[sectionId] || []).map(faq => 
        faq.id === faqId ? { ...faq, ...updatedFaq } : faq
      )
    }));
  };

  const handleDeleteFaq = (sectionId, faqId) => {
    if (!window.confirm('Are you sure you want to delete this FAQ?')) return;
    
    // For temp FAQs (not yet saved), just remove from local state
    // For existing FAQs, mark for deletion and remove from local state
    const faq = (sectionFaqs[sectionId] || []).find(f => f.id === faqId);
    
    if (faq && !faq.id.startsWith('temp_')) {
      // Mark existing FAQ for deletion
      if (!window.deletedFaqs) window.deletedFaqs = [];
      window.deletedFaqs.push({ sectionId, faqId });
    }
    
    // Remove from local state
    setSectionFaqs(prev => ({
      ...prev,
      [sectionId]: (prev[sectionId] || []).filter(faq => faq.id !== faqId)
    }));
  };

  const handleReorderFaqs = (sectionId, event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;
    
    const currentFaqs = sectionFaqs[sectionId] || [];
    const oldIndex = currentFaqs.findIndex(faq => faq.id === active.id);
    const newIndex = currentFaqs.findIndex(faq => faq.id === over.id);
    
    if (oldIndex !== -1 && newIndex !== -1) {
      const reorderedFaqs = arrayMove(currentFaqs, oldIndex, newIndex);
      // Update sort_order for each FAQ
      const updatedFaqs = reorderedFaqs.map((faq, index) => ({
        ...faq,
        sort_order: index
      }));
      
      setSectionFaqs(prev => ({
        ...prev,
        [sectionId]: updatedFaqs
      }));
    }
  };

  // Gallery Management Functions
  const handleGalleryImageUpload = async (e, sectionId) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    if (!validImageTypes.includes(file.type)) {
      alert('Please upload an image file (JPG, PNG, GIF)');
      return;
    }
    
    // Check file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }

    setUploadingGalleryImage(true);
    
    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await api.post(`/admin/campaigns/${campaignId}/campaign-sections/${sectionId}/gallery`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Update local gallery state
      setSectionGallery(prev => ({
        ...prev,
        [sectionId]: [...(prev[sectionId] || []), response.data.galleryItem]
      }));
      
      // Clear the input
      e.target.value = '';
    } catch (error) {
      console.error('Gallery upload error:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error occurred';
      alert('Failed to upload image: ' + errorMessage);
    } finally {
      setUploadingGalleryImage(false);
    }
  };

  const handleUpdateGalleryImage = (sectionId, imageId, updatedData) => {
    // Update local gallery state only - no API call until save
    setSectionGallery(prev => ({
      ...prev,
      [sectionId]: (prev[sectionId] || []).map(image => 
        image.id === imageId ? { ...image, ...updatedData } : image
      )
    }));
  };

  const handleDeleteGalleryImage = async (sectionId, imageId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;
    
    try {
      // Make API call to delete the image
      await api.delete(`/admin/campaigns/${campaignId}/campaign-sections/${sectionId}/gallery/${imageId}`);
      
      // Remove from local state after successful deletion
      setSectionGallery(prev => ({
        ...prev,
        [sectionId]: (prev[sectionId] || []).filter(image => image.id !== imageId)
      }));
    } catch (error) {
      console.error('Failed to delete gallery image:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error occurred';
      alert('Failed to delete image: ' + errorMessage);
    }
  };

  const toggleCollapse = (sectionId) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // Function to save all FAQ changes - to be called from parent when saving
  const saveFaqChanges = useCallback(async () => {
    try {
      // First, handle deletions
      if (window.deletedFaqs && window.deletedFaqs.length > 0) {
        for (const { sectionId, faqId } of window.deletedFaqs) {
          try {
            await api.delete(`/admin/campaigns/${campaignId}/campaign-sections/${sectionId}/faqs/${faqId}`);
          } catch (error) {
            console.error('Failed to delete FAQ:', error);
          }
        }
        // Clear the deletion list
        window.deletedFaqs = [];
      }

      // Then handle creates and updates
      for (const [sectionId, faqs] of Object.entries(sectionFaqs)) {
        // Sort FAQs by their current sort_order to ensure proper processing
        const sortedFaqs = [...faqs].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
        
        console.log(`Saving FAQs for section ${sectionId}:`, sortedFaqs.map(f => ({ id: f.id, question: f.question.substring(0, 30), sort_order: f.sort_order })));
        
        for (let index = 0; index < sortedFaqs.length; index++) {
          const faq = sortedFaqs[index];
          try {
            if (faq.id.startsWith('temp_')) {
              // This is a new FAQ that needs to be created
              console.log(`Creating new FAQ: question="${faq.question?.substring(0, 20)}", answer="${faq.answer?.substring(0, 20)}", sort_order=${index}`);
              
              // Ensure we have valid values
              if (!faq.question || !faq.answer) {
                console.error('Invalid new FAQ data:', faq);
                continue;
              }
              
              const response = await api.post(`/admin/campaigns/${campaignId}/campaign-sections/${sectionId}/faqs`, {
                question: String(faq.question),
                answer: String(faq.answer),
                sort_order: Number(index) // Use the index as the sort order
              });
              // Update the FAQ with the real ID from the server
              setSectionFaqs(prev => ({
                ...prev,
                [sectionId]: prev[sectionId].map(f => 
                  f.id === faq.id ? { ...f, id: response.data.faq.id, sort_order: index } : f
                )
              }));
            } else {
              // This is an existing FAQ that needs to be updated
              console.log(`Updating FAQ ${faq.id}: question="${faq.question?.substring(0, 20)}", answer="${faq.answer?.substring(0, 20)}", sort_order=${index} (type: ${typeof index})`);
              
              // Ensure we have valid values
              if (!faq.question || !faq.answer) {
                console.error('Invalid FAQ data:', faq);
                continue;
              }
              
              const requestData = {
                question: String(faq.question),
                answer: String(faq.answer),
                sort_order: Number(index) // Ensure sort_order is a number
              };
              
              console.log('Sending FAQ update request:', requestData);
              
              await api.put(`/admin/campaigns/${campaignId}/campaign-sections/${sectionId}/faqs/${faq.id}`, requestData);
              
              // Update local state to reflect the saved sort_order
              setSectionFaqs(prev => ({
                ...prev,
                [sectionId]: prev[sectionId].map(f => 
                  f.id === faq.id ? { ...f, sort_order: index } : f
                )
              }));
            }
          } catch (error) {
            console.error('Failed to save FAQ:', error);
            // Continue with other FAQs even if one fails
          }
        }
      }
    } catch (error) {
      console.error('Error saving FAQ changes:', error);
    }
  }, [campaignId, api, sectionFaqs]);

  // Function to save all gallery changes - to be called from parent when saving
  const saveGalleryChanges = useCallback(async () => {
    try {
      // First, handle deletions
      if (window.deletedGalleryImages && window.deletedGalleryImages.length > 0) {
        for (const { sectionId, imageId } of window.deletedGalleryImages) {
          try {
            await api.delete(`/admin/campaigns/${campaignId}/campaign-sections/${sectionId}/gallery/${imageId}`);
          } catch (error) {
            console.error('Failed to delete gallery image:', error);
          }
        }
        // Clear the deletion list
        window.deletedGalleryImages = [];
      }

      // Then handle updates for existing images
      for (const [sectionId, images] of Object.entries(sectionGallery)) {
        for (const image of images) {
          try {
            // Only update existing images (not temp ones)
            if (!image.id.startsWith('temp_')) {
              await api.put(`/admin/campaigns/${campaignId}/campaign-sections/${sectionId}/gallery/${image.id}`, {
                title: image.title || '',
                description: image.description || ''
              });
            }
          } catch (error) {
            console.error('Failed to save gallery image:', error);
            // Continue with other images even if one fails
          }
        }
      }
    } catch (error) {
      console.error('Error saving gallery changes:', error);
    }
  }, [campaignId, api, sectionGallery]);

  // Pass the saveFaqChanges function to parent
  useEffect(() => {
    if (onSaveFaqChanges) {
      onSaveFaqChanges(saveFaqChanges);
    }
  }, [onSaveFaqChanges, saveFaqChanges]);

  // Pass the saveGalleryChanges function to parent
  useEffect(() => {
    if (onSaveGalleryChanges) {
      onSaveGalleryChanges(saveGalleryChanges);
    }
  }, [onSaveGalleryChanges, saveGalleryChanges]);

  const sectionTypes = [
    { type: 'banner', label: 'Banner', icon: Images },
    { type: 'content', label: 'Content', icon: FileText },
    { type: 'forms', label: 'Form', icon: ClipboardList },
    { type: 'faqs', label: 'FAQs', icon: CircleQuestionMark },
    { type: 'gallery', label: 'Gallery', icon: Palette }
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h4 className="text-lg font-semibold text-gray-900">Page Sections</h4>
          <p className="text-sm text-gray-600">Drag and drop to reorder sections. Each section type can be added multiple times.</p>
        </div>
        <div className="relative add-section-dropdown">
          <button
            type="button"
            onClick={() => setShowAddDropdown(!showAddDropdown)}
            className="flex items-center space-x-2 px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-teal-600 text-sm font-medium"
          >
            <Plus className="w-4 h-4" />
            <span>Add Section</span>
            <svg 
              className={`w-4 h-4 transition-transform ${showAddDropdown ? 'rotate-180' : ''}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {showAddDropdown && (
            <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-20">
              <div className="py-2">
                <div className="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Section Types
                </div>
                {sectionTypes.map(({ type, label, icon: TypeIcon }) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => handleAddSection(type)}
                    className="w-full flex items-center space-x-3 px-3 py-2 text-left hover:bg-gray-50 text-sm"
                  >
                    <TypeIcon className="w-5 h-5 text-gray-600 shrink-0" />
                    <div>
                      <div className="font-medium text-gray-900">{label}</div>
                      <div className="text-xs text-gray-500">
                        {type === 'banner' && 'Background image section'}
                        {type === 'content' && 'About section with optional form'}
                        {type === 'forms' && 'Entry form section'}
                        {type === 'faqs' && 'Frequently asked questions'}
                        {type === 'gallery' && 'Image gallery section'}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {localSections.length === 0 ? (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
          <p className="text-gray-500 mb-4">No sections added yet.</p>
          <p className="text-sm text-gray-400">Click the + buttons above to add sections to your campaign page.</p>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={localSections.map(s => s.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-4">
              {localSections.map((section) => (
                <SortableSection
                  key={section.id}
                  section={section}
                  isCollapsed={collapsedSections[section.id] || false}
                  onToggleCollapse={toggleCollapse}
                  onUpdate={handleUpdateSection}
                  onDelete={handleDeleteSection}
                  onDuplicate={handleDuplicateSection}
                  uploadingBackground={uploadingBackground}
                  onBackgroundUpload={handleBackgroundUpload}
                  sectionFaqs={sectionFaqs}
                  newFaqs={newFaqs}
                  setNewFaqs={setNewFaqs}
                  handleAddFaq={handleAddFaq}
                  handleUpdateFaq={handleUpdateFaq}
                  handleDeleteFaq={handleDeleteFaq}
                  handleReorderFaqs={handleReorderFaqs}
                  faqSensor={faqSensor}
                  sectionGallery={sectionGallery}
                  uploadingGalleryImage={uploadingGalleryImage}
                  handleGalleryImageUpload={handleGalleryImageUpload}
                  handleDeleteGalleryImage={handleDeleteGalleryImage}
                  handleUpdateGalleryImage={handleUpdateGalleryImage}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
};

export default SectionManager;